import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import PublicRoute from './components/PublicRoute'

// Componentes pequenos - manter imports estáticos
import Login from './pages/Login'
import Register from './pages/Register'
import Unauthorized from './pages/Unauthorized'
import Home from './pages/Home'
import Profile from './pages/Profile'
import Listas from './pages/Listas'
import AuthCallback from './pages/AuthCallback'

// Páginas grandes - usar lazy loading (code splitting)
const Participar = lazy(() => import('./pages/Participar'))
const Resultados = lazy(() => import('./pages/Resultados'))
const Usuarios = lazy(() => import('./pages/Usuarios'))
const Admin = lazy(() => import('./pages/Admin'))
const ParametrosSorteio = lazy(() => import('./pages/ParametrosSorteio'))
const GerenciarSorteios = lazy(() => import('./pages/GerenciarSorteios'))
const Configuracoes = lazy(() => import('./pages/Configuracoes'))
const ListaNegativa = lazy(() => import('./pages/ListaNegativa'))
const ParticipantesAnteriores = lazy(() => import('./pages/ParticipantesAnteriores'))
const Campanhas = lazy(() => import('./pages/Campanhas'))
const DetalhesCampanha = lazy(() => import('./pages/DetalhesCampanha'))
const Administration = lazy(() => import('./pages/Administration'))
const SessoesAtivas = lazy(() => import('./pages/SessoesAtivas'))
const Auditoria = lazy(() => import('./pages/Auditoria'))

// Componente de loading para Suspense
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
  </div>
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <Routes>
            {/* Rotas públicas */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Rotas protegidas */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Home />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/participar/:id" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Participar />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/resultados" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Resultados />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/campanhas" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Campanhas />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/campanhas/:campanhaId" element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <DetalhesCampanha />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/usuarios" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Usuarios />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Admin />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/parametros-sorteio" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ParametrosSorteio />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/gerenciar-sorteios" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <GerenciarSorteios />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/configuracoes" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Configuracoes />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/sessoes" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SessoesAtivas />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/auditoria" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Auditoria />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Administration />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/listas" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Listas />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/lista-negativa" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ListaNegativa />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/participantes-anteriores" element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ParticipantesAnteriores />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            } />
          </Routes>
          
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 6000, // 6 segundos para dar tempo de ler
              style: {
                background: '#363636',
                color: '#fff',
              },
              error: {
                duration: 8000, // 8 segundos para erros (mais tempo para ler)
              },
              success: {
                duration: 5000, // 5 segundos para sucesso
              },
            }}
          />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
